/**
 * Cloudflare Access Identity Utilities
 * Fetches the signed-in user identity from Cloudflare Access
 *
 * For local development without Cloudflare Access, you can stub identity by
 * setting VITE_STUB_ACCESS_IDENTITY=true in your .env (or .env.local) file.
 */

import { fetchTestbedIdentity, isTestbed } from './testbed'

export interface AccessIdentity {
  email?: string
  name?: string
}

const SHOULD_STUB_IDENTITY = import.meta.env.VITE_STUB_ACCESS_IDENTITY === 'true'
const STUB_IDENTITY: AccessIdentity = {
  email: import.meta.env.VITE_STUB_ACCESS_EMAIL || 'local.dev@localhost',
  name: import.meta.env.VITE_STUB_ACCESS_NAME || 'Local Dev',
}

/**
 * Fetch user identity from Cloudflare Access.
 *
 * In testbed mode (`VITE_TESTBED=1`, see `./testbed`) there is no Cloudflare Access: the page
 * origin cannot answer `/cdn-cgi/access/get-identity`, so it is never called and the identity
 * comes from the testbed proxy instead.
 */
export async function fetchAccessIdentity(): Promise<AccessIdentity | null> {
  if (isTestbed()) {
    return fetchTestbedIdentity()
  }

  if (SHOULD_STUB_IDENTITY) {
    return STUB_IDENTITY
  }

  try {
    const response = await fetch('/cdn-cgi/access/get-identity', {
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Identity request failed with status ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    // In dev, allow silent stub fallback if requested
    if (SHOULD_STUB_IDENTITY) {
      console.warn('Falling back to stubbed identity (Cloudflare Access not available in dev)')
      return STUB_IDENTITY
    }

    console.warn('Failed to fetch Cloudflare Access identity:', error)
    throw error
  }
}

/**
 * Derive avatar initials from identity
 * @returns Uppercase initials (1-2 characters)
 */
export function getAvatarInitials(identity: AccessIdentity | null | undefined): string {
  if (!identity) {
    return '?'
  }

  // Prefer full name: split by space, use first letters of first+last
  if (identity.name) {
    const nameParts = identity.name.trim().split(/\s+/)
    if (nameParts.length >= 2) {
      // First letter of first name + first letter of last name
      return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
    } else if (nameParts.length === 1 && nameParts[0].length > 0) {
      // Single name: use first letter
      return nameParts[0][0].toUpperCase()
    }
  }

  // Fallback to email: use local-part before @, take first 2 letters
  if (identity.email) {
    const localPart = identity.email.split('@')[0]
    if (localPart.length >= 2) {
      return localPart.substring(0, 2).toUpperCase()
    } else if (localPart.length === 1) {
      return localPart[0].toUpperCase()
    }
  }

  return '?'
}

/**
 * Get display name from identity
 */
export function getDisplayName(identity: AccessIdentity | null | undefined): string {
  if (!identity) {
    return 'Signed in'
  }

  return identity.name || identity.email || 'Signed in'
}
